import { createPortal } from 'react-dom'
import LoaderOverlay from '../LoaderOverlay'
import useModalEnter from '../../hooks/useModalEnter'

/* "MORE MARKET" — the modal behind the button on every non-cricket game
 * details page.
 *
 * Reference (admin bundle `admin/layout.html`):
 *
 *   getMarketList() {
 *     store.state.isLoading = true                 // full-screen loader FIRST
 *     callApi('game/marketlist', { gid: gameId }).then(r => {
 *       marketList = r.status == 200 ? r.data.t1 : []
 *       store.state.isLoading = false              // loader off…
 *       $refs.marketListModal.show()               // …then the modal opens
 *     })
 *   }
 *
 *   <b-modal ref="marketListModal" hide-footer title="MORE MARKET">
 *     <div class="table-responsive more-market">
 *       <table class="table">
 *         <thead><tr><th>Market Name</th><th class="text-right">Market Book</th></tr></thead>
 *         <tbody>
 *           <tr v-for="m in marketList">
 *             <td><a href="javascript:void(0)" @click="setMarketId(m.mid)">{{m.mname}}</a></td>
 *             <td class="text-right">{{m.scnt}}</td>
 *           </tr>
 *           <tr v-else><td colspan="2">No record found</td></tr>
 *         </tbody>
 *       </table>
 *     </div>
 *   </b-modal>
 *
 * So: loader overlay while the list loads, then the modal fades/slides in —
 * which is exactly what `useModalEnter` + `LoaderOverlay` already do for
 * CasinoResultModal, so the same two are used here. `modal-md` and the
 * `__BVID__1749` id are the reference's own (user's capture, 2026-09-22).
 *
 * Clicking a market name in the reference calls `setMarketId(mid)`, which
 * pulls that market into the page. Ours scrolls to the market's block and
 * closes — the page already renders every market the feed returns.
 */

const MODAL_ID = '__BVID__1749'

function MoreMarketModal({ show, loading, markets = [], onClose, onPick }) {
  // not "entered" while the loader is up — the modal mounts only once the
  // list is here, so its open animation starts from the loader, not before it
  const { entered, backdropClass, onBackdropClick } = useModalEnter(onClose, show && !loading)

  if (!show) return null
  if (loading) return createPortal(<LoaderOverlay />, document.body)

  return createPortal(
    <>
      <div className={backdropClass}></div>
      <div
        id={MODAL_ID}
        role="dialog"
        aria-labelledby={`${MODAL_ID}___BV_modal_title_`}
        aria-describedby={`${MODAL_ID}___BV_modal_body_`}
        className={`modal fade${entered ? ' show' : ''}`}
        aria-modal="true"
        style={{ display: 'block' }}
        onClick={onBackdropClick}
      >
        <div className="modal-dialog modal-md" onClick={(e) => e.stopPropagation()}>
          <span tabIndex="0"></span>
          <div id={`${MODAL_ID}___BV_modal_content_`} tabIndex="-1" className="modal-content">
            <header id={`${MODAL_ID}___BV_modal_header_`} className="modal-header">
              <h5 id={`${MODAL_ID}___BV_modal_title_`} className="modal-title">MORE MARKET</h5>
              <button type="button" aria-label="Close" className="close" onClick={onClose}>&times;</button>
            </header>
            <div id={`${MODAL_ID}___BV_modal_body_`} className="modal-body">
              <div className="table-responsive more-market">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Market Name</th>
                      <th className="text-right">Market Book</th>
                    </tr>
                  </thead>
                  <tbody>
                    {markets.length > 0 ? markets.map((m) => (
                      <tr key={m.mid}>
                        <td>
                          <a href="javascript:void(0)" onClick={() => onPick?.(m)}>{m.mname}</a>
                        </td>
                        <td className="text-right">{m.book}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan="2">No record found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <span tabIndex="0"></span>
        </div>
      </div>
    </>,
    document.body,
  )
}

export default MoreMarketModal
